import { csrfHeaders } from '../../core/csrf.js';

/**
 * Composant Modal
 * Gère les modales avec backdrop blur
 *
 * Sélecteurs sémantiques : .ui-modal, .ui-modal-background, .ui-modal-close
 */

import { logger } from '../../core/base.js';

/**
 * Initialise les modales standard
 */
export function initModals() {
    document.querySelectorAll('.ui-modal[id]').forEach(modal => {
        let opener;
        const focusable = () => [...modal.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')]
            .filter(node => !node.disabled && node.tabIndex >= 0 && node.getClientRects().length);
        const close = () => {
            modal.classList.remove('is-active');
            if (opener?.isConnected) opener.focus();
        };
        modal.addEventListener('keydown', event => {
            if (!modal.classList.contains('is-active')) return;
            if (event.key === 'Escape') { event.preventDefault(); close(); }
            if (event.key === 'Tab') {
                const nodes = focusable();
                const first = nodes[0];
                const last = nodes[nodes.length - 1];
                if (!first) { event.preventDefault(); return; }
                if (event.shiftKey && (document.activeElement === first || document.activeElement === modal)) {
                    event.preventDefault(); last.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault(); first.focus();
                }
            }
        });

        // Boutons pour ouvrir la modale
        document.querySelectorAll('button[data-modal=' + modal.id + ']').forEach(button => {
            button.addEventListener('click', event => {
                opener = button;
                modal.classList.add('is-active');
                modal.setAttribute('role', 'dialog');
                modal.setAttribute('aria-modal', 'true');
                modal.tabIndex = -1;
                (focusable()[0] || modal).focus();
            });
        });

        // Éléments pour fermer la modale
        modal.querySelectorAll('.ui-modal-background, button.ui-modal-close, button.arcate-cancel').forEach(c => {
            c.addEventListener('click', event => {
                close();
            });
        });

        // Forms inside modals.
        modal.querySelectorAll('form').forEach(form => {
            form.addEventListener('submit', event => {
                event.preventDefault();

                form.classList.remove('arcate-error');
                form.classList.add('arcate-submitting');

                const formData = new FormData(event.target);

                fetch(event.target.action, {
                    method: event.target.method,
                    headers: csrfHeaders(event.target.action),
                    body: formData
                })
                    .then(response => response.text())
                    .then(data => {
                        form.classList.remove('arcate-submitting');
                        modal.classList.remove('is-active');
                    })
                    .catch(error => {
                        form.classList.remove('arcate-submitting');
                        form.classList.add('arcate-error');

                        logger.error("Erreur lors de la soumission de la modale:", error);
                    });
            });
        });
    });

    logger.debug('Modals initialized');
}

/**
 * Initialise les modales formulaires avec événements personnalisés
 */
export function initModalForms() {
    document.querySelectorAll('form.ui-modal').forEach(modal => {
        modal.addEventListener('submit', event => {
            event.preventDefault();

            event.target.classList.remove('is-active');

            event.target.dispatchEvent(new CustomEvent('modalSubmit', {
                detail: Object.fromEntries(new FormData(event.target))
            }));
        });

        modal.addEventListener('reset', event => {
            event.target.classList.remove('is-active');
        });
    });

    logger.debug('Modal forms initialized');
}

// Auto-initialisation
document.addEventListener('DOMContentLoaded', () => {
    initModals();
    initModalForms();
});
